// VizDemo — 벡터수학엔진 개발용 데모 라우트 (#vizdemo / #geodemo)
// §2-1 예시 스크립트 3종을 렌더해 draw-on·자동계산·라벨회피·스텝제어를 눈으로 검증한다.
// 특이점 좌표는 스크립트에 없다 — intercepts/intersections/extrema 스텝이 전부 자동 계산.
import React from "react";
import { MathViz } from "../ui/mathviz/MathViz.jsx";
import { GeoFeedback, GeoInsight } from "../ui/GeoFeedback.jsx";

// GeoInsight(채점 결과 자동 부착형) 상시 샘플 — 합성 잉크: 삼각형만 그리고 높이는 안 그린 학생
const _seg=(a,b,n=24)=>({pts:Array.from({length:n+1},(_,i)=>({x:a[0]+(b[0]-a[0])*i/n,y:a[1]+(b[1]-a[1])*i/n,p:.5})),col:"var(--ink)",sz:1});
const GEO_INK_SAMPLE={pages:[[_seg([100,600],[700,600]),_seg([700,600],[400,150]),_seg([400,150],[100,600])]],w:800,h:750};
// 사각형 샘플: 사각형만 그리고 대각선은 안 그린 학생
const GEO_INK_QUAD={pages:[[_seg([150,150],[650,180]),_seg([650,180],[700,600]),_seg([700,600],[120,560]),_seg([120,560],[150,150])]],w:800,h:750};
// 원 샘플: 원만 그리고 반지름은 표시 안 한 학생 (손떨림 포함)
const GEO_INK_CIRCLE={pages:[[{pts:Array.from({length:49},(_,i)=>{
  const a=2*Math.PI*i/48, wob=((i*7)%9-4);
  return {x:400+(210+wob)*Math.cos(a), y:390+(210+wob)*Math.sin(a), p:.5};
}),col:"var(--ink)",sz:1}]],w:800,h:750};
import { MathText } from "../ui/math.jsx";

/* ⑥ 해설 렌더 통합 검증: AI 해설 문자열(마크다운+$$수식$$+mathviz블록+기존 svg)이
   MathText 한 번으로 전부 렌더되는지 — RICH_FMT 전환의 회귀 감시용 샘플 */
const SAMPLE_EXPLANATION=[
  "## 두 곡선 사이 넓이",
  "$f(x)=e^x-2$ 와 $g(x)=\\ln(x+2)$ 는 **역함수 관계**라 $y=x$ 대칭이야.",
  "$$S=\\int_{a}^{b}\\{g(x)-f(x)\\}\\,dx$$",
  "```mathviz",
  JSON.stringify({version:1,theme:"algebra",view:{x:[-3,4.6],y:[-3,4.6]},steps:[
    {type:"axes",ticks:1},
    {type:"plot",id:"f",expr:"exp(x)-2",domain:[-3,1.85],color:"accent"},
    {type:"plot",id:"g",expr:"log(x+2)",domain:[-1.86,4.6],color:"chalk"},
    {type:"intersections",of:["f","g"]},
    {type:"area",between:["f","g"],range:"auto-intersections"},
    {type:"pill",text:"교점이 적분 한계"}]}),
  "```",
  "옛 해설의 인라인 SVG도 그대로 나온다:",
  '<svg viewBox="0 0 200 80" width="200" height="80"><line x1="10" y1="70" x2="190" y2="70" stroke="var(--ink)"/><path d="M20 60 Q100 -20 180 60" fill="none" stroke="var(--ok)" stroke-width="2"/><text x="95" y="78" font-size="11" fill="var(--ink)">x</text></svg>',
  "깨진 블록은 조용히 생략된다:",
  "```mathviz",
  "{이건 JSON이 아님",
  "```",
].join("\n");

const { useState, useEffect } = React;

/* ① 삼각형 넓이 (geometry) — 4단계 기하 상호작용의 정답 모델과 같은 언어 */
const DEMO_TRIANGLE={
  version:1, theme:"geometry",
  view:{ x:[-1,7.2], y:[-1,5.6] },
  steps:[
    { type:"axes", ticks:1 },
    { type:"segment", from:[1,1],   to:[6,1],   color:"chalk" },
    { type:"segment", from:[6,1],   to:[2.5,4.5], color:"chalk" },
    { type:"segment", from:[2.5,4.5], to:[1,1], color:"chalk" },
    { type:"point", at:[2.5,4.5], label:"A" },
    { type:"point", at:[1,1],     label:"B" },
    { type:"point", at:[6,1],     label:"C" },
    { type:"segment", from:[2.5,4.5], to:[2.5,1], color:"fix", dash:true },
    { type:"point", at:[2.5,1], label:"H" },
    { type:"lines", tex:["BC=5,\\quad AH=3.5","S=\\tfrac12\\cdot BC\\cdot AH"], mutedExceptLast:true },
    { type:"formula", tex:"S=\\tfrac12\\cdot 5\\cdot 3.5=\\tfrac{35}{4}", box:true },
    { type:"chip", text:"보조선 = 높이" },
    { type:"pill", text:"넓이는 밑변과 높이부터 — 높이가 없으면 그려 넣는다" },
  ],
};

/* ② 지수·로그 점근선 — 지시서 §2-1 예시 그대로 (절편·교점·넓이 전부 자동) */
const DEMO_EXPLOG={
  version:1, theme:"sequence",
  view:{ x:[-3,4.6], y:[-3,4.6] },
  steps:[
    { type:"axes", ticks:1 },
    { type:"plot", id:"f", expr:"exp(x)-2",  domain:[-3,1.85],   color:"accent" },
    { type:"plot", id:"g", expr:"log(x+2)",  domain:[-1.86,4.6], color:"chalk" },
    { type:"asymptote", axis:"h", at:-2, label:"y=-2" },
    { type:"asymptote", axis:"v", at:-2, label:"x=-2" },
    { type:"intercepts", of:"f" },
    { type:"intersections", of:["f","g"] },
    { type:"area", between:["f","g"], range:"auto-intersections" },
    { type:"chip", text:"교점 = 적분 한계" },
    { type:"pill", text:"두 곡선 사이 넓이는 교점부터 잡는다" },
  ],
};

/* ③ 쌍곡선 초점 — c²=a²+b² 자동 계산 (√6 ≈ 2.449가 어디에도 안 적혀 있음) */
const DEMO_HYPERBOLA={
  version:1, theme:"geometry",
  view:{ x:[-6,6], y:[-4,4] },
  steps:[
    { type:"axes", ticks:1 },
    { type:"conic", kind:"hyperbola", a:2, b:1.414, show:["asymptotes","vertices","foci"] },
    { type:"formula", tex:"\\frac{x^2}{4}-\\frac{y^2}{2}=1", box:true },
    { type:"lines", tex:["c^2=a^2+b^2=4+2","c=\\sqrt{6}"], mutedExceptLast:true },
    { type:"chip", text:"초점 자동 계산" },
    { type:"pill", text:"쌍곡선의 초점은 점근선보다 바깥에 있다" },
  ],
};

/* ④ 벡터 내적(투영) — 깨진 유니코드·엇나간 각도 호를 vector/angle 스텝으로 정확히 재현 */
const DEMO_DOT={
  version:1, theme:"sequence",   // 시그니처 연보라 계열 accent
  view:{ x:[-0.6,6.4], y:[-1.2,3.6] },
  steps:[
    { type:"vector", from:[0,0], to:[5,0],     label:"\\vec{a}", color:"accent" },
    { type:"vector", from:[0,0], to:[3,2.4],   label:"\\vec{b}", color:"chalk" },
    { type:"angle",  at:[0,0], from:[5,0], to:[3,2.4], label:"θ" },
    { type:"segment", from:[3,2.4], to:[3,0], dash:true, color:"muted" },
    { type:"segment", from:[0,-0.55], to:[3,-0.55], color:"point", width:2.4,
      label:"|\\vec{b}|\\cos θ (투영)" },
    { type:"point", at:[3,0], label:null },
    { type:"formula", tex:"\\vec{a}\\cdot\\vec{b}=|\\vec{a}|\\,|\\vec{b}|\\cos\\theta", box:true },
    { type:"pill", text:"눌러서 투영한 길이 × 나머지 벡터의 크기" },
  ],
};

/* ═══ 기출·교과 풀이 시안 ═══
   위 ①~④가 "엔진이 무엇을 그릴 수 있나"라면, 아래는 "실제 시험문제 한 개를 끝까지 풀어내나".
   좌표는 여전히 안 적는다 — 초점·극점·접점은 전부 스텝이 계산한다. */

/* ⑦ 수능 미적분 30번 — 역함수 조건 g(2x)=2f(x), ∫₁⁸xf'(x)dx
   f는 조건만으로 하나로 정해지지 않는다. 그래서 곡선은 "조건을 만족하는 f의 한 예"이고,
   네 점 (1,1)(2,2)(4,4)(8,8)을 지나며 구간마다 y=x의 위·아래가 바뀌는 구조는 정확하다.
   (x·exp(−0.3·ln2·sin(π·log₂x)) — f(2ⁿ)=2ⁿ, 전 구간 증가를 수치 검증함) */
const DEMO_CALC30={
  version:1, theme:"algebra",
  view:{ x:[-0.7,9.3], y:[-0.7,9.3], equal:true },
  steps:[
    { type:"axes", ticks:1 },
    { type:"segment", from:[0,0], to:[9.1,9.1], color:"muted", dash:true, label:"y=x" },
    { type:"plot", id:"f", expr:"x*exp(-0.207944*sin(pi*log(x)/0.693147))",
      domain:[1,8.7], color:"accent" },
    { type:"point", at:[1,1], label:"f(1)=1" },
    { type:"lines", tex:["g(2x)=2f(x)","\\Leftrightarrow\\ f(2f(x))=2x"], mutedExceptLast:true },
    { type:"chip", text:"역함수는 되돌리기 — g를 f로 넘긴다" },
    { type:"point", at:[2,2], label:"f(2)=2" },
    { type:"point", at:[4,4], label:"f(4)=4" },
    { type:"point", at:[8,8], label:"f(8)=8" },
    { type:"lines", tex:["x=1:\\ f(2f(1))=f(2)=2","x=2:\\ f(4)=4\\ \\Rightarrow\\ x=4:\\ f(8)=8"],
      mutedExceptLast:true },
    { type:"area", between:["f"], range:[1,2] },
    { type:"area", between:["f"], range:[2,4] },
    { type:"area", between:["f"], range:[4,8] },
    { type:"lines", tex:["t=2f(x)\\ \\Rightarrow\\ \\int_2^4\\!f=4\\!\\int_1^2\\!xf'(x)dx=7",
      "\\int_4^8\\!f=4\\!\\int_2^4\\!xf'(x)dx=20"], mutedExceptLast:true },
    { type:"lines", tex:["\\int_1^8\\!f=\\tfrac54+7+20=\\tfrac{113}{4}"], mutedExceptLast:false },
    { type:"formula", tex:"\\int_1^8\\!xf'(x)dx=\\Big[xf(x)\\Big]_1^8-\\int_1^8\\!f(x)dx=63-\\tfrac{113}{4}", box:true },
    { type:"formula", tex:"=\\tfrac{139}{4}\\ \\Rightarrow\\ p+q=143", box:true },
    { type:"pill", text:"f를 못 구해도 답은 나온다 — 넓이만 옮기면 된다" },
  ],
};

/* ⑧ 수능 기하 26번 — 타원의 두 초점과 접원
   A·B는 풀이가 확정한 점이지만, 두 접선과 접점은 tangentsFrom이 계산한다(테스트로 역검증). */
const _A26=[Math.sqrt(37), 3*Math.sqrt(3)/2];      // 타원 위 제1사분면 점
const _B26=[0, -4.5*Math.sqrt(3)];                 // 원 C의 중심 (y축 음수부)
const DEMO_GEO26={
  version:1, theme:"geometry",
  view:{ x:[-10.6,10.6], y:[-17.6,5.6], equal:true },
  steps:[
    { type:"axes", ticks:4 },
    { type:"conic", kind:"ellipse", a:8, b:4, show:["foci"] },
    { type:"formula", tex:"\\frac{x^2}{64}+\\frac{y^2}{16}=1,\\quad c=\\sqrt{64-16}=4\\sqrt3", box:true },
    { type:"point", at:_A26, label:"A" },
    { type:"segment", from:_A26, to:[4*Math.sqrt(3),0], color:"chalk" },
    { type:"segment", from:_A26, to:[-4*Math.sqrt(3),0], color:"chalk" },
    { type:"lines", tex:["\\overline{AF}+\\overline{AF'}=2a=16"], mutedExceptLast:false },
    { type:"chip", text:"타원의 정의" },
    { type:"circle", center:_B26, r:9, tangentsFrom:_A26, show:["center"], centerLabel:"B" },
    { type:"segment", from:[4*Math.sqrt(3),0], to:_B26, color:"chalk" },
    { type:"segment", from:[-4*Math.sqrt(3),0], to:_B26, color:"chalk" },
    { type:"segment", from:_A26, to:_B26, color:"fix", dash:true },
    { type:"lines", tex:["\\square AFBF'=\\triangle ABF+\\triangle ABF'",
      "=\\tfrac12 r\\,\\overline{AF}+\\tfrac12 r\\,\\overline{AF'}=\\tfrac12 r(\\overline{AF}+\\overline{AF'})"],
      mutedExceptLast:true },
    { type:"formula", tex:"8r=72\\ \\Rightarrow\\ r=9\\quad(\\text{정답 ②})", box:true },
    { type:"pill", text:"대각선으로 갈라 반지름을 높이로 쓴다 — A의 위치는 필요 없다" },
  ],
};

/* ⑨ 수학Ⅱ 대표 유형 — 삼차함수의 극값과 접선 (극점·접선 전부 자동) */
const DEMO_CUBIC={
  version:1, theme:"algebra",
  view:{ x:[-2.8,3.1], y:[-4.2,4.6] },
  steps:[
    { type:"axes", ticks:1 },
    { type:"plot", id:"f", expr:"x^3-3x", domain:[-2.4,2.5], color:"accent" },
    { type:"extrema", of:"f" },
    { type:"lines", tex:["f'(x)=3x^2-3=3(x+1)(x-1)","f'(x)=0\\ \\Rightarrow\\ x=\\pm1"],
      mutedExceptLast:true },
    // 기울기 9라 halfLen 기본값(1.4)이면 접선 양 끝이 뷰 밖으로 나가 통째로 사라진다
    { type:"tangent", of:"f", at:2, halfLen:0.38 },
    { type:"lines", tex:["f'(2)=9,\\ f(2)=2"], mutedExceptLast:false },
    { type:"formula", tex:"y=9(x-2)+2=9x-16", box:true },
    { type:"chip", text:"미분계수 = 접선의 기울기" },
    { type:"pill", text:"극값은 f'=0에서, 접선은 그 점의 미분계수에서" },
  ],
};

/* ⑩ 수학Ⅰ 대표 유형 — 삼각함수의 주기·최대·최소 (최댓값·최솟값 자동 검출) */
const DEMO_TRIG={
  version:1, theme:"sequence",
  view:{ x:[-0.5,6.9], y:[-2.4,4.4] },
  steps:[
    { type:"axes", ticks:1 },
    { type:"plot", id:"f", expr:"2sin(2x)+1", domain:[0,6.6], color:"accent" },
    { type:"asymptote", axis:"h", at:1, label:"y=1 (중심선)" },
    { type:"extrema", of:"f" },
    { type:"lines", tex:["y=a\\sin(bx)+c\\ \\Rightarrow\\ a=2,\\ b=2,\\ c=1",
      "\\text{주기}=\\tfrac{2\\pi}{|b|}=\\pi"], mutedExceptLast:true },
    { type:"formula", tex:"\\text{최댓값}=c+|a|=3,\\quad \\text{최솟값}=c-|a|=-1", box:true },
    { type:"chip", text:"진폭 2, 주기 π" },
    { type:"pill", text:"a는 진폭, b는 주기, c는 위아래 이동" },
  ],
};

/* ⑪ 기하 — 포물선의 정의(초점거리 = 준선거리). 초점은 conic이 계산한다 */
const _P11=[3, 2*Math.sqrt(6)];
const DEMO_PARABOLA={
  version:1, theme:"geometry",
  view:{ x:[-3.6,8.2], y:[-7.1,7.1], equal:true },
  steps:[
    { type:"axes", ticks:2 },
    { type:"conic", kind:"parabola", p:2, show:["foci","vertices"] },
    { type:"formula", tex:"y^2=8x=4\\cdot 2\\cdot x\\ \\Rightarrow\\ p=2", box:true },
    { type:"asymptote", axis:"v", at:-2, label:"준선 x=-2" },
    { type:"point", at:_P11, label:"P" },
    { type:"segment", from:_P11, to:[2,0], color:"chalk", label:"5" },
    { type:"segment", from:_P11, to:[-2,_P11[1]], color:"fix", dash:true, label:"5" },
    { type:"lines", tex:["포물선\\ 위의\\ 점은\\ 초점과\\ 준선에서\\ 같은\\ 거리",
      "\\overline{PF}=x_P-(-2)=x_P+2"], mutedExceptLast:true },
    { type:"formula", tex:"x_P+2=5\\ \\Rightarrow\\ x_P=3", box:true },
    { type:"chip", text:"포물선의 정의" },
    { type:"pill", text:"초점까지 재지 말고 준선까지 재면 그냥 x좌표다" },
  ],
};

/* ⑫ 미적분 — 곡선과 직선 사이 넓이. 교점(±√5)은 스크립트에 없다 */
const DEMO_AREA={
  version:1, theme:"algebra",
  view:{ x:[-3.6,3.6], y:[-4.4,4.4], equal:true },
  steps:[
    { type:"axes", ticks:1 },
    { type:"plot", id:"f", expr:"x^3-4x", domain:[-2.5,2.5], color:"accent" },
    { type:"plot", id:"g", expr:"x", domain:[-2.7,2.7], color:"chalk" },
    { type:"intersections", of:["f","g"] },
    { type:"area", between:["f","g"], range:"auto-intersections" },
    { type:"lines", tex:["x^3-4x=x\\ \\Rightarrow\\ x(x^2-5)=0","x=0,\\ \\pm\\sqrt5"],
      mutedExceptLast:true },
    { type:"chip", text:"교점 = 적분 한계" },
    { type:"lines", tex:["원점\\ 대칭이라\\ 한쪽만\\ 재서\\ 두\\ 배",
      "S=2\\int_0^{\\sqrt5}\\!\\{x-(x^3-4x)\\}dx=2\\int_0^{\\sqrt5}\\!(5x-x^3)dx"],
      mutedExceptLast:true },
    { type:"formula", tex:"S=2\\Big[\\tfrac52x^2-\\tfrac14x^4\\Big]_0^{\\sqrt5}=\\tfrac{25}{4}", box:true },
    { type:"pill", text:"교점부터 잡고, 대칭이면 반만 재라" },
  ],
};

/* ⑬ 기하 — 내적으로 사이각을 얻어 합벡터의 크기를 구한다 */
const _B13=[1, Math.sqrt(3)];
const DEMO_VECSUM={
  version:1, theme:"sequence",
  view:{ x:[-0.7,5.2], y:[-1,3.4], equal:true },
  steps:[
    { type:"vector", from:[0,0], to:[3,0], label:"\\vec{a}", color:"accent" },
    { type:"vector", from:[0,0], to:_B13, label:"\\vec{b}", color:"chalk" },
    { type:"angle", at:[0,0], from:[3,0], to:_B13, label:"θ" },
    { type:"lines", tex:["\\vec a\\cdot\\vec b=|\\vec a||\\vec b|\\cos\\theta=3\\cdot2\\cos\\theta=3",
      "\\cos\\theta=\\tfrac12\\ \\Rightarrow\\ \\theta=60^\\circ"], mutedExceptLast:true },
    { type:"segment", from:[3,0], to:[4,Math.sqrt(3)], color:"muted", dash:true },
    { type:"segment", from:_B13, to:[4,Math.sqrt(3)], color:"muted", dash:true },
    { type:"vector", from:[0,0], to:[4,Math.sqrt(3)], label:"\\vec{a}+\\vec{b}", color:"point" },
    { type:"lines", tex:["|\\vec a+\\vec b|^2=|\\vec a|^2+2\\,\\vec a\\cdot\\vec b+|\\vec b|^2",
      "=9+6+4=19"], mutedExceptLast:true },
    { type:"formula", tex:"|\\vec a+\\vec b|=\\sqrt{19}", box:true },
    { type:"chip", text:"내적 = 사이각 정보" },
    { type:"pill", text:"크기는 제곱해서 구한다 — 내적이 교차항으로 들어온다" },
  ],
};

const DEMOS=[
  { key:"tri",  name:"① 삼각형 넓이",     script:DEMO_TRIANGLE },
  { key:"log",  name:"② 지수·로그 점근선", script:DEMO_EXPLOG },
  { key:"hyp",  name:"③ 쌍곡선 초점",     script:DEMO_HYPERBOLA },
  { key:"dot",  name:"④ 벡터 내적(투영)", script:DEMO_DOT },
];

/* 공간도형 사선 투영 — RICH_FMT 규칙 4의 (x,y,z)→(x+0.4y, z+0.4y) 공식을 그대로 적용한
   한 변 2 정육면체. AI가 지시대로 산수하면 정확히 이 모양이 나와야 한다(회귀 기준).
   풀이 순서: 밑면 대각선 DB(2√2) → 수직 모서리 BF(2) → 직각삼각형 DBF의 빗변 DF(2√3). */
const DEMO_SPACE={
  version:1, theme:"geometry",
  view:{ x:[-0.7,3.6], y:[-0.8,3.5] },
  steps:[
    { type:"segment", from:[0,0],     to:[2,0], label:"2", labelAt:[1,-0.42] },      // 가로 2 — 라벨은 도형 밖(아래)
    { type:"segment", from:[2,0],     to:[2,2] },
    { type:"segment", from:[2,2],     to:[0,2] },
    { type:"segment", from:[0,2],     to:[0,0], label:"2", labelAt:[-0.42,1] },      // 높이 2 — 도형 밖(왼쪽)
    { type:"segment", from:[0,2],     to:[0.8,2.8] },                // 윗면 보이는 모서리
    { type:"segment", from:[2,2],     to:[2.8,2.8] },
    { type:"segment", from:[0.8,2.8], to:[2.8,2.8] },
    { type:"segment", from:[2,0],     to:[2.8,0.8], label:"2", labelAt:[2.6,0.12] }, // 깊이 2 — 도형 밖(오른쪽 아래)
    { type:"segment", from:[2.8,0.8], to:[2.8,2.8] },
    { type:"segment", from:[0,0],     to:[0.8,0.8], dash:true },     // 숨은 모서리 3개
    { type:"segment", from:[0.8,0.8], to:[2.8,0.8], dash:true },
    { type:"segment", from:[0.8,0.8], to:[0.8,2.8], dash:true },
    { type:"segment", from:[0.8,0.8], to:[2,0], dash:true, color:"accent",
      label:"\\overline{DB}=2\\sqrt{2}" },                           // ① 밑면 대각선 먼저
    { type:"segment", from:[0.8,0.8], to:[2,2], dash:true, color:"accent",
      label:"\\overline{DF}=2\\sqrt{3}", labelAt:[1.62,1.22] },      // ② 직각삼각형 DBF의 빗변 (B 위가 F)
    { type:"point", at:[0,0],     label:"A" },
    { type:"point", at:[2,0],     label:"B" },
    { type:"point", at:[2.8,0.8], label:"C" },
    { type:"point", at:[0.8,0.8], label:"D" },
    { type:"point", at:[0,2],     label:"E" },
    { type:"point", at:[2,2],     label:"F" },
    { type:"point", at:[2.8,2.8], label:"G" },
    { type:"point", at:[0.8,2.8], label:"H" },
    { type:"pill", text:"공간좌표 (x,y,z) → 화면 (x+0.4y, z+0.4y) — 숨은 모서리는 점선" },
  ],
};

// 기출·교과 풀이 — 문제 한 개를 처음부터 답까지 (버튼 두 번째 줄)
const SOLVES=[
  { key:"space", name:"공간도형 투영", badge:"기하",
    q:"한 변의 길이가 $2$인 정육면체 $ABCD\\text{-}EFGH$에서 대각선 $\\overline{DF}$의 길이를 구하시오.",
    script:DEMO_SPACE },
  { key:"c30", name:"미적분 30번",  badge:"수능 4점",
    q:"실수 전체의 집합에서 증가하고 미분가능한 함수 $f(x)$가 $f(1)=1$, $\\int_1^2 f(x)dx=\\frac54$를 만족시킨다. "+
      "$f(x)$의 역함수를 $g(x)$라 할 때, $x\\ge 1$인 모든 실수 $x$에 대하여 $g(2x)=2f(x)$이다. "+
      "$\\int_1^8 xf'(x)dx=\\frac{q}{p}$일 때 $p+q$의 값은? (단, $p$와 $q$는 서로소인 자연수이다.)",
    script:DEMO_CALC30 },
  { key:"g26", name:"기하 26번",    badge:"수능 3점",
    q:"두 초점이 $F$, $F'$인 타원 $\\frac{x^2}{64}+\\frac{y^2}{16}=1$ 위의 점 중 제1사분면에 있는 점 $A$가 있다. "+
      "두 직선 $AF$, $AF'$에 동시에 접하고 중심이 $y$축 위에 있는 원 중 중심의 $y$좌표가 음수인 것을 $C$라 하자. "+
      "원 $C$의 중심을 $B$라 할 때 사각형 $AFBF'$의 넓이가 $72$이다. 원 $C$의 반지름의 길이는?",
    script:DEMO_GEO26 },
  { key:"cub", name:"삼차함수 접선", badge:"수학Ⅱ",
    q:"곡선 $y=x^3-3x$의 극값을 구하고, 이 곡선 위의 점 $(2,\\,2)$에서의 접선의 방정식을 구하시오.",
    script:DEMO_CUBIC },
  { key:"trg", name:"삼각함수 주기", badge:"수학Ⅰ",
    q:"함수 $y=2\\sin 2x+1$의 주기와 최댓값·최솟값을 구하시오.",
    script:DEMO_TRIG },
  { key:"par", name:"포물선 준선",   badge:"기하",
    q:"포물선 $y^2=8x$ 위의 점 $P$에서 초점 $F$까지의 거리가 $5$일 때, 점 $P$의 $x$좌표를 구하시오.",
    script:DEMO_PARABOLA },
  { key:"are", name:"곡선 사이 넓이", badge:"미적분",
    q:"곡선 $y=x^3-4x$와 직선 $y=x$로 둘러싸인 부분의 넓이를 구하시오.",
    script:DEMO_AREA },
  { key:"vec", name:"벡터의 크기",   badge:"기하",
    q:"$|\\vec a|=3$, $|\\vec b|=2$이고 $\\vec a\\cdot\\vec b=3$일 때, $|\\vec a+\\vec b|$의 값을 구하시오.",
    script:DEMO_VECSUM },
];

function VizDemo(){
  const geoHash=()=>/geodemo/i.test(location.hash);
  const [tab,setTab]=useState(geoHash()?"geo":"tri");
  const [theme,setTheme]=useState("light");   // 기본 = 앱 브랜드 라이트 (대표 결정)
  const [runId,setRunId]=useState(0);          // key 갈아끼워 처음부터 재생
  useEffect(()=>{
    const f=()=>{ if(geoHash())setTab("geo"); };
    window.addEventListener("hashchange",f);
    return ()=>window.removeEventListener("hashchange",f);
  },[]);
  const demo=DEMOS.find(d=>d.key===tab)||SOLVES.find(d=>d.key===tab);
  const solve=SOLVES.find(d=>d.key===tab);
  return (
    <div style={{maxWidth:solve?1120:640,margin:"0 auto",padding:"26px 16px 60px",
      display:"flex",flexDirection:"column",gap:16}}>
      <h1 style={{fontFamily:"'Jua',sans-serif",fontSize:22,textAlign:"center",margin:0}}>
        벡터 수학 렌더링 데모
      </h1>
      <p style={{textAlign:"center",color:"var(--sub)",fontSize:13,margin:0}}>
        교점·절편·초점은 전부 함수식에서 자동 계산 — 스크립트에 좌표가 없습니다
      </p>
      <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
        {DEMOS.map(d=>(
          <button key={d.key} className="btn gho" onClick={()=>{setTab(d.key);setRunId(r=>r+1);}}
            style={{fontSize:13,padding:"8px 14px",
              opacity:tab===d.key?1:.55,fontWeight:tab===d.key?700:400}}>
            {d.name}
          </button>
        ))}
        <button className="btn gho" onClick={()=>setTab("geo")}
          style={{fontSize:13,padding:"8px 14px",
            opacity:tab==="geo"?1:.55,fontWeight:tab==="geo"?700:400}}>
          ⑤ 기하 채점
        </button>
        <button className="btn gho" onClick={()=>setTab("mt")}
          style={{fontSize:13,padding:"8px 14px",
            opacity:tab==="mt"?1:.55,fontWeight:tab==="mt"?700:400}}>
          ⑥ 해설 렌더
        </button>
      </div>
      <div style={{borderTop:"1px solid var(--line)",paddingTop:12,
        display:"flex",flexDirection:"column",gap:8,alignItems:"center"}}>
        <p style={{margin:0,fontSize:12,color:"var(--sub)"}}>
          기출·교과 풀이 — 문제 하나를 처음부터 답까지
        </p>
        <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
          {SOLVES.map(d=>(
            <button key={d.key} className="btn gho" onClick={()=>{setTab(d.key);setRunId(r=>r+1);}}
              style={{fontSize:13,padding:"8px 14px",
                opacity:tab===d.key?1:.55,fontWeight:tab===d.key?700:400}}>
              {d.name}
              <span style={{fontSize:10.5,marginLeft:6,opacity:.7}}>{d.badge}</span>
            </button>
          ))}
        </div>
      </div>
      {tab!=="geo"&&(
        <div style={{display:"flex",gap:8,justifyContent:"center"}}>
          <button className="btn gho" style={{fontSize:12,padding:"6px 12px"}}
            onClick={()=>setTheme(t=>t==="dark"?"light":"dark")}>
            테마: {theme==="dark"?"🌙 다크 칠판":"☀️ 라이트"}
          </button>
          <button className="btn gho" style={{fontSize:12,padding:"6px 12px"}}
            onClick={()=>setRunId(r=>r+1)}>
            ⟲ 처음부터
          </button>
        </div>
      )}
      {tab==="geo"&&(
        <>
          <GeoFeedback/>
          <div style={{borderTop:"1px solid var(--line)",paddingTop:14,display:"flex",flexDirection:"column",gap:8}}>
            <p style={{margin:0,fontSize:12.5,color:"var(--sub)",textAlign:"center"}}>
              ↓ 실전에서는 별도 메뉴 없이 — 학습·시험 채점 결과에 이렇게 자동으로 붙습니다 (기하 문항 + 작도 감지 시)
            </p>
            <GeoInsight ink={GEO_INK_SAMPLE} question="삼각형 ABC의 넓이를 구하시오. (필요한 보조선 포함)"/>
            <GeoInsight ink={GEO_INK_QUAD} question="사각형 ABCD의 넓이를 구하시오. (필요한 보조선 포함)"/>
            <GeoInsight ink={GEO_INK_CIRCLE} question="원 O의 넓이를 구하시오. (필요한 표시 포함)"/>
          </div>
        </>
      )}
      {tab==="mt"&&(
        <div className="card" style={{padding:"16px 18px"}}>
          <MathText text={SAMPLE_EXPLANATION} tag="div"/>
        </div>
      )}
      {solve&&(
        <div className="card" style={{padding:"13px 15px",fontSize:13,lineHeight:1.65}}>
          <div style={{fontSize:11.5,color:"var(--sub)",marginBottom:5,letterSpacing:".02em"}}>
            문제 · {solve.badge}
          </div>
          <MathText text={solve.q} tag="div"/>
        </div>
      )}
      {tab!=="geo"&&tab!=="mt"&&demo&&(
        <div style={{display:"flex",justifyContent:"center"}}>
          <MathViz key={demo.key+theme+runId} script={demo.script} theme={theme}
            controls autoplay split={!!solve}/>
        </div>
      )}
    </div>
  );
}

export { VizDemo };
